export const AGENT_CHAT_PDF_MIME_TYPE = "application/pdf";
export const AGENT_CHAT_DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * Binary document types handled by the web fallback adapter. Images and text
 * files are handled by assistant-ui's dedicated adapters.
 */
export const AGENT_CHAT_FILE_ATTACHMENT_ACCEPT = [
  AGENT_CHAT_PDF_MIME_TYPE,
  AGENT_CHAT_DOCX_MIME_TYPE,
  ".pdf",
  ".docx",
].join(",");

/** Native document-picker types. Video/audio and legacy .doc are deliberate omissions. */
export const AGENT_CHAT_DOCUMENT_PICKER_TYPES = [
  AGENT_CHAT_PDF_MIME_TYPE,
  AGENT_CHAT_DOCX_MIME_TYPE,
  "text/plain",
  "text/markdown",
  "image/*",
] as const;

export const AGENT_CHAT_UNSUPPORTED_ATTACHMENT_MESSAGE =
  "That file type is not supported. Upload a PDF, DOCX, TXT, or image file.";

type AttachmentMetadata = {
  type?: unknown;
  name?: unknown;
  mimeType?: unknown;
};

const GENERIC_MIME_TYPES = new Set(["", "application/octet-stream"]);
const TEXT_FILE_EXTENSIONS = new Set([
  ".csv",
  ".html",
  ".json",
  ".md",
  ".rtf",
  ".txt",
  ".xml",
]);

function normalizedMimeType(value: unknown) {
  return typeof value === "string"
    ? (value.split(";", 1)[0] ?? "").trim().toLowerCase()
    : "";
}

function fileExtension(value: unknown) {
  if (typeof value !== "string") return "";
  const clean = value.trim().toLowerCase();
  const dotIndex = clean.lastIndexOf(".");
  return dotIndex >= 0 ? clean.slice(dotIndex) : "";
}

/**
 * Runtime boundary used by both clients and the API. A generic/missing MIME
 * may fall back to a safe document extension, while an explicitly unsupported
 * MIME (for example video/mp4) is never rescued by a renamed filename.
 */
export function isSupportedAgentChatAttachment(attachment: unknown): boolean {
  if (!attachment || typeof attachment !== "object") return false;
  const candidate = attachment as AttachmentMetadata;
  const type = candidate.type;
  if (type !== "image" && type !== "file") return false;

  const mimeType = normalizedMimeType(candidate.mimeType);
  const extension = fileExtension(candidate.name);

  if (type === "image") {
    return mimeType.startsWith("image/");
  }

  if (mimeType.startsWith("text/")) return true;
  if (
    mimeType === AGENT_CHAT_PDF_MIME_TYPE ||
    mimeType === AGENT_CHAT_DOCX_MIME_TYPE
  ) {
    return true;
  }
  if (!GENERIC_MIME_TYPES.has(mimeType)) return false;

  return (
    extension === ".pdf" ||
    extension === ".docx" ||
    TEXT_FILE_EXTENSIONS.has(extension)
  );
}

const LEGACY_UNREADABLE_ATTACHMENT_CONTEXT =
  /Attached file "[^"]*" could not be read by Tabbio\. Ask the user to upload a PDF, DOCX, TXT, or image file instead\.$/s;
const LEGACY_EXTRACTED_ATTACHMENT_CONTEXT =
  /Attached file: [^\n]*\nMIME type: [^\n]*\n\n[\s\S]*$/;

/**
 * Removes attachment extraction/instruction parts persisted by older chat
 * runs. The authored user text lives in its own part and is preserved.
 */
export function stripInternalAgentAttachmentText(text: string): string {
  const withoutUnreadable = text.replace(
    LEGACY_UNREADABLE_ATTACHMENT_CONTEXT,
    "",
  );
  const sanitized = withoutUnreadable.replace(
    LEGACY_EXTRACTED_ATTACHMENT_CONTEXT,
    "",
  );
  return sanitized === text ? text : sanitized.trimEnd();
}

function sanitizeTextPart(part: unknown): unknown | null {
  if (!part || typeof part !== "object") return part;
  const candidate = part as { type?: unknown; text?: unknown };
  if (candidate.type !== "text" || typeof candidate.text !== "string") {
    return part;
  }

  const text = stripInternalAgentAttachmentText(candidate.text);
  return text ? { ...candidate, text } : null;
}

/** Sanitizes a persisted user-message content value without mutating it. */
export function sanitizeAgentUserMessageContent(content: unknown): unknown {
  if (typeof content === "string") {
    return stripInternalAgentAttachmentText(content);
  }

  if (Array.isArray(content)) {
    return content.map(sanitizeTextPart).filter((part) => part !== null);
  }

  if (!content || typeof content !== "object") return content;
  const candidate = content as { format?: unknown; parts?: unknown };
  if (candidate.format !== 2 || !Array.isArray(candidate.parts)) return content;

  return {
    ...candidate,
    parts: candidate.parts
      .map(sanitizeTextPart)
      .filter((part) => part !== null),
  };
}
